import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PreviewTable from './PreviewTable';
import * as api from './api';
import { useToasts } from '../ScheduleCoordination/Toasts';
import { defaultMonth, shiftMonth, syncLocationMonth } from '../ScheduleCoordination/constants';
import { LoadingMask } from '../ScheduleCoordination/shared';

/**
 * 店長排班預覽（步驟 4）。
 *
 * 版面沿用 sched.css 的設計語言（.sc-card / .sc-stats / .sc-table / .sc-badge），
 * 這樣六個步驟長得像同一套系統 —— ⛔ 不要在這裡另外發明一組 styled-components 樣式。
 *
 * 資訊層級（圖② 下半）：
 *   ① 缺什麼、哪天、哪個時段、可否補位  → 最上面的摘要磚 ＋「要先處理的缺額」卡
 *   ② 長條月表                          → 主體
 *   ③ 問題清單（含待補資料）            → 輔助，可收合
 */
const initialPeriod = (props) => {
    const year = parseInt(props.year, 10);
    const month = parseInt(props.month, 10);

    if (year >= 2000 && year <= 2100 && month >= 1 && month <= 12) {
        return { year, month };
    }

    return defaultMonth();
};

const STATUS_BADGE = {
    published: 'sc-badge--green',
    locked: 'sc-badge--orange',
};

const SchedPreview = (props) => {
    const [toastLayer, notify] = useToasts();
    const [period, setPeriod] = useState(() => initialPeriod(props));
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    // 目前展開的缺額（要指派補位的那一列）
    const [openKey, setOpenKey] = useState(null);
    const [candidates, setCandidates] = useState(null);
    const [picked, setPicked] = useState('');
    // 「待補資料」常常一次就是幾十筆（整個月的開診表沒填），預設收合免得把
    // 真正要處理的硬性缺額淹掉。⛔ 不是隱藏 —— 數量與展開入口一直都在。
    const [showPending, setShowPending] = useState(false);

    const load = useCallback(
        async (year, month, { silent } = {}) => {
            if (!silent) setLoading(true);
            try {
                setPayload(await api.fetchPreview(year, month));
            } catch (err) {
                notify('error', '讀取排班預覽失敗，請重新整理頁面');
            } finally {
                setLoading(false);
            }
        },
        [notify]
    );

    useEffect(() => {
        syncLocationMonth(period.year, period.month);
        load(period.year, period.month);
    }, [period, load]);

    const problems = (payload && payload.problems) || [];

    const summary = useMemo(() => {
        let hard = 0;
        let soft = 0;
        let pending = 0;
        const hardDates = {};
        problems.forEach((p) => {
            if (p.is_pending_data) {
                pending += 1;
            } else if (p.enforcement === 'hard') {
                hard += 1;
                hardDates[p.date] = true;
            } else {
                soft += 1;
            }
        });
        return { hard, soft, pending, hardDays: Object.keys(hardDates).length };
    }, [problems]);

    const problemKey = (p) => `${p.date}|${p.session}|${p.rule_version_id}`;

    // 整個月一天都沒被套用的規則（每條只列一次）—— 這是「畫面全綠但其實沒在檢查」的根因
    const dormantRules = useMemo(() => {
        const skips = (payload && payload.skips) || [];
        const dates = ((payload && payload.dates) || []).length;
        const byVersion = {};

        skips.forEach((sk) => {
            if (sk.reason !== 'out_of_effective_range' && sk.reason !== 'out_of_scope') return;
            const row = byVersion[sk.rule_version_id] || { ...sk, days: 0 };
            row.days += 1;
            byVersion[sk.rule_version_id] = row;
        });

        return Object.values(byVersion).filter((r) => dates > 0 && r.days >= dates);
    }, [payload]);

    const hardProblems = problems.filter((p) => !p.is_pending_data && p.enforcement === 'hard');
    // 表格實際列出的項目：硬性／柔性永遠列出，待補資料只有展開時才列
    const visibleProblems = showPending ? problems : problems.filter((p) => !p.is_pending_data);

    const openCover = useCallback(
        async (problem) => {
            const key = problemKey(problem);

            if (openKey === key) {
                setOpenKey(null);
                return;
            }

            setOpenKey(key);
            setCandidates(null);
            setPicked('');

            if (!problem.skill_id) {
                setCandidates({
                    candidates: [],
                    reason: '這條規則沒有指定職能（要求的是全部人員），無法用機動補位處理。請改為請人調班。',
                });
                return;
            }

            if (!problem.allow_floater_cover) {
                setCandidates({
                    candidates: [],
                    reason: '這條規則設定為「缺額不可由機動人力補位」。請改為請人調班，或由具覆寫權限者填原因放行。',
                });
                return;
            }

            try {
                setCandidates(await api.fetchCandidates(problem.date, problem.session, problem.skill_id));
            } catch (err) {
                setCandidates({ candidates: [], reason: '讀取候選人失敗，請重新整理頁面' });
            }
        },
        [openKey]
    );

    const runWrite = useCallback(
        async (fn) => {
            setBusy(true);
            try {
                const res = await fn();
                notify('success', res.message || '已更新');
                setOpenKey(null);
                await load(period.year, period.month, { silent: true });
            } catch (err) {
                notify('error', err.message);
            } finally {
                setBusy(false);
            }
        },
        [load, notify, period]
    );

    const handleAssign = (problem) => {
        if (!picked) {
            notify('error', '請先選擇要補位的同仁');
            return;
        }
        runWrite(() => api.assignCover(problem.date, problem.session, picked, problem.skill_id, ''));
    };

    const scrollToDate = useCallback((date) => {
        const el = document.querySelector(`[data-problem-date="${date}"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    if (loading && !payload) {
        return (
            <div className="sc-card">
                {toastLayer}
                <div className="sc-empty">讀取中…</div>
            </div>
        );
    }

    if (!payload) {
        return (
            <div className="sc-card sc-card--accent-red">
                {toastLayer}
                <div className="sc-empty">目前無法取得排班預覽資料，請重新整理頁面。</div>
            </div>
        );
    }

    const skillNameOf = (id) => {
        const hit = (payload.skills || []).filter((s) => s.id === id)[0];
        return hit ? hit.name : '全部人員';
    };

    /** 缺額的一列：日期 / 時段 / 缺什麼 / 可否補位 / 操作 —— 補位表單就地展開 */
    const coverRow = (problem) => {
        const key = problemKey(problem);
        const open = openKey === key;
        const level = problem.is_pending_data
            ? { cls: 'sc-badge--orange', text: '待補' }
            : problem.enforcement === 'hard'
            ? { cls: 'sc-badge--red', text: '硬性' }
            : { cls: 'sc-badge--grey', text: '提醒' };
        const coverable = !problem.is_pending_data && !!problem.skill_id && !!problem.allow_floater_cover;

        return (
            <React.Fragment key={key}>
                <tr
                    data-problem-date={problem.date}
                    className={problem.enforcement === 'hard' && !problem.is_pending_data ? 'is-danger' : ''}
                >
                    <td className="is-nowrap">
                        <strong>{problem.date}</strong>
                        <span className="sc-sub">{problem.session_label}</span>
                    </td>
                    <td>
                        <span className={`sc-badge ${level.cls}`}>{level.text}</span>
                    </td>
                    <td>
                        <strong>{problem.message}</strong>
                        <span className="sc-sub">
                            規則：{problem.rule_name}
                            {problem.rendered_sentence ? `　${problem.rendered_sentence}` : ''}
                        </span>
                    </td>
                    <td>
                        {problem.is_pending_data ? (
                            <span className="sc-badge sc-badge--grey">先補開診表</span>
                        ) : coverable ? (
                            // ⚠️「規則允許」≠「真的補得到人」—— 有沒有合資格的機動人力，
                            //    要按「指派機動補位」展開才知道，展開後會直接說明原因。
                            <span className="sc-badge sc-badge--green">規則允許</span>
                        ) : (
                            <span className="sc-badge sc-badge--orange">規則不允許</span>
                        )}
                    </td>
                    <td className="is-act">
                        {problem.is_pending_data ? (
                            <a className="sc-btn sc-btn--xs" href={payload.month_url}>
                                前往本月設定
                            </a>
                        ) : (
                            <button
                                type="button"
                                className={`sc-btn sc-btn--xs${open ? '' : ' sc-btn--primary'}`}
                                disabled={busy}
                                onClick={() => openCover(problem)}
                            >
                                {open ? '收合' : '指派機動補位'}
                            </button>
                        )}
                    </td>
                </tr>
                {open && (
                    <tr>
                        <td colSpan={5} style={{ background: 'var(--sc-surface-alt)' }}>
                            {!candidates && <span className="sc-help">讀取候選人…</span>}
                            {candidates && candidates.candidates.length === 0 && (
                                <div className="sc-note sc-note--orange" style={{ margin: 0 }}>
                                    <strong>這一格補不了：</strong>
                                    {candidates.reason}
                                </div>
                            )}
                            {candidates && candidates.candidates.length > 0 && (
                                <div className="sc-inline">
                                    <div className="sc-field" style={{ width: '130px' }}>
                                        <label>日期</label>
                                        <input className="sc-input" readOnly value={problem.date} />
                                    </div>
                                    <div className="sc-field" style={{ width: '110px' }}>
                                        <label>時段</label>
                                        <input className="sc-input" readOnly value={problem.session_label} />
                                    </div>
                                    <div className="sc-field" style={{ width: '150px' }}>
                                        <label>補位職能</label>
                                        <input className="sc-input" readOnly value={skillNameOf(problem.skill_id)} />
                                    </div>
                                    <div className="sc-field" style={{ width: '260px' }}>
                                        <label>來源（只有機動人力）</label>
                                        <select
                                            className="sc-input"
                                            value={picked}
                                            onChange={(e) => setPicked(e.target.value)}
                                        >
                                            <option value="">選擇補位的同仁</option>
                                            {candidates.candidates.map((c) => (
                                                <option key={c.uuid} value={c.uuid}>
                                                    {c.name}
                                                    {c.current_cover_skill_name
                                                        ? `（目前補 ${c.current_cover_skill_name}，會改成本職能）`
                                                        : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        type="button"
                                        className="sc-btn sc-btn--primary"
                                        disabled={busy}
                                        onClick={() => handleAssign(problem)}
                                    >
                                        指派
                                    </button>
                                    <span className="sc-help" style={{ flex: '1 1 100%', margin: 0 }}>
                                        指派後，這位同仁在這個時段只會計入被指派的那一個職能，⛔ 不再計入其他職能。
                                    </span>
                                </div>
                            )}
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    return (
        <div>
            {toastLayer}

            <header className="sched-head">
                <div className="sched-head__main">
                    <h1 className="sched-head__title">
                        排班預覽
                        <span className="sched-head__period">
                            {payload.year} 年 {payload.month} 月
                        </span>
                        <span className={`sc-badge ${STATUS_BADGE[payload.status] || 'sc-badge--grey'}`}>
                            {payload.status_label}
                        </span>
                        {summary.hard > 0 && (
                            <span className="sc-badge sc-badge--solid-red">{summary.hardDays} 天有缺額</span>
                        )}
                    </h1>
                    <p className="sched-head__lede">
                        員工為列、日期為欄，橫向一次看完整月、縱向比較所有員工。缺額直接標在同一張表上。
                    </p>
                </div>
                <div className="sched-head__actions">
                    <button
                        type="button"
                        className="sc-btn"
                        onClick={() => setPeriod(shiftMonth(payload.year, payload.month, -1))}
                    >
                        ← 上個月
                    </button>
                    <button type="button" className="sc-btn" onClick={() => setPeriod(defaultMonth())}>
                        本月
                    </button>
                    <button
                        type="button"
                        className="sc-btn"
                        onClick={() => setPeriod(shiftMonth(payload.year, payload.month, 1))}
                    >
                        下個月 →
                    </button>
                    <a className="sc-btn sc-btn--primary" href={payload.publish_url}>
                        前往發布 →
                    </a>
                </div>
            </header>

            <div className="sc-stats">
                <div className={`sc-stats__item ${summary.hard ? 'sc-stats__item--red' : 'sc-stats__item--green'}`}>
                    <span className="sc-stats__label">硬性缺額</span>
                    <span className="sc-stats__value">
                        {summary.hard}
                        <small>項</small>
                    </span>
                    <span className="sc-stats__note">
                        {summary.hard ? `分布在 ${summary.hardDays} 天，發布會被擋下` : '目前發布不會被擋'}
                    </span>
                </div>
                <div className={`sc-stats__item ${summary.soft ? 'sc-stats__item--orange' : 'sc-stats__item--muted'}`}>
                    <span className="sc-stats__label">柔性提醒</span>
                    <span className="sc-stats__value">
                        {summary.soft}
                        <small>項</small>
                    </span>
                    <span className="sc-stats__note">不擋發布</span>
                </div>
                <div className={`sc-stats__item ${summary.pending ? 'sc-stats__item--orange' : 'sc-stats__item--muted'}`}>
                    <span className="sc-stats__label">待補資料</span>
                    <span className="sc-stats__value">
                        {summary.pending}
                        <small>項</small>
                    </span>
                    <span className="sc-stats__note">開診表未設定 → 規則沒被套用</span>
                </div>
                <div className="sc-stats__item sc-stats__item--muted">
                    <span className="sc-stats__label">名單人數</span>
                    <span className="sc-stats__value">
                        {(payload.members || []).length}
                        <small>人</small>
                    </span>
                    <span className="sc-stats__note">機動人力 {(payload.floaters || []).length} 人</span>
                </div>
            </div>

            {(payload.skills || []).length === 0 && (
                <div className="sc-note sc-note--orange">
                    <strong>尚未設定任何職能</strong>，所有最低在班人數規則都算不到任何人。 請先到{' '}
                    <a href={payload.setup_url}>排班基礎設定</a> 建立職能並指派成員。
                </div>
            )}

            {/* 多職能的人沒指定本月主要職能 ⇒ 他不計入任何職能，職能人數會看起來莫名其妙少一個。
                ⛔ 不可以靜默 —— 少了這一段，店長只會看到「護理師 1 人」卻找不出原因。 */}
            {(payload.duty_pending || []).length > 0 && (
                <div className="sc-note sc-note--orange">
                    <strong>有 {(payload.duty_pending || []).length} 位同仁的「本月主要職能」待指定</strong>
                    ，他們掛了兩個以上職能。一個人每個時段只計一次，沒指定要算哪一個，
                    他們目前 <strong>不計入任何職能</strong>（總人數仍算得到人）：
                    <ul>
                        {(payload.duty_pending || []).map((d) => (
                            <li key={d.uuid}>
                                {d.name}　本月有 {(d.dates || []).length} 天上班受影響
                            </li>
                        ))}
                    </ul>
                    請到 <a href={payload.setup_url}>排班基礎設定 → 人員屬性</a> 指定，指定前 ⛔ 無法發布。
                </div>
            )}

            {/* ⚠️ 規則存在但整個月都沒被套用時一定要講出來：
                   不講的話畫面就是一整排綠色 ✓，看起來像「規則都通過」，
                   實際上根本沒有人在檢查（2026-10-17 就是這樣被誤讀的）。 */}
            {dormantRules.length > 0 && (
                <div className="sc-note sc-note--orange">
                    <strong>有 {dormantRules.length} 條規則這個月完全沒有生效</strong>
                    ，所以月表上那些日期顯示的不是「通過」，而是「沒有規則在看」：
                    <ul>
                        {dormantRules.map((r) => (
                            <li key={r.rule_version_id}>{r.message}</li>
                        ))}
                    </ul>
                    要讓它們管到這個月，請到 <a href={payload.rules_url || '/work-schedule/sched-rules'}>規則庫</a>{' '}
                    調整該規則的「有效期間」。
                </div>
            )}

            {/* ① 最醒目：缺什麼、哪天、哪個時段、可否補位 */}
            <section className={`sc-card ${summary.hard ? 'sc-card--accent-red' : 'sc-card--accent-green'}`}>
                <div className="sc-card__head">
                    <h2 className="sc-card__title">要先處理的缺額</h2>
                    <span className="sc-card__hint">
                        處理順序：① 請人改期　② 指派機動補位　③ 都不可行時才到發布頁覆寫放行
                    </span>
                </div>
                {hardProblems.length === 0 ? (
                    <div className="sc-empty">
                        <strong>硬性人力規則全部通過</strong>
                        {summary.pending > 0
                            ? `仍有 ${summary.pending} 項「待補資料」（開診表未設定），在下方問題清單展開。`
                            : '目前沒有任何會擋住發布的缺額。'}
                    </div>
                ) : (
                    <div className="sc-scroll">
                        <table className="sc-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '130px' }}>日期／時段</th>
                                    <th style={{ width: '76px' }}>等級</th>
                                    <th>缺什麼／為什麼</th>
                                    <th style={{ width: '104px' }}>可否補位</th>
                                    <th style={{ width: '150px' }} className="is-act">
                                        操作
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{hardProblems.map(coverRow)}</tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* ② 主體：長條月表 */}
            <section className="sc-card">
                <div className="sc-card__head">
                    <h2 className="sc-card__title">長條月表</h2>
                    <span className="sc-card__hint">
                        正＝上班　休＝一般休　特＝特休（半天加 AM／PM）　機動→○○＝這個時段補該職能
                    </span>
                </div>
                <PreviewTable payload={payload} onDeficitClick={scrollToDate} />
                <div className="sc-card__foot">
                    表頭橘底線＝那一天有硬性缺額；員工格橘框＝造成缺額的那一格；職能列顯示「在班／需求」，不足時加橘框。
                    職能列<strong>上下午人數相同時只寫一個數字</strong>，不同時才拆成「上 / 下」兩行。
                    規則檢核列：<strong>✓＝有規則檢查過且通過</strong>，<strong>✓＊＝通過，但還有規則沒被套用</strong>（滑過去看原因），
                    <strong>—＝這一天沒有任何規則被套用</strong>（⛔ 不等於通過）。
                    姓名欄在橫向捲動時固定在左邊，⛔ 不會跟著捲走。
                </div>
            </section>

            {/* ③ 輔助：完整問題清單 */}
            <section className="sc-card">
                <div className="sc-card__head">
                    <h2 className="sc-card__title">問題清單（{problems.length} 項）</h2>
                    <span className="sc-card__hint">
                        硬性 {summary.hard}・柔性 {summary.soft}・待補資料 {summary.pending}
                    </span>
                    {summary.pending > 0 && (
                        <div className="sc-card__act">
                            <button type="button" className="sc-btn sc-btn--xs" onClick={() => setShowPending((v) => !v)}>
                                {showPending ? '收合待補資料' : `展開待補資料（${summary.pending} 項）`}
                            </button>
                        </div>
                    )}
                </div>
                {visibleProblems.length === 0 ? (
                    <div className="sc-empty">
                        {summary.pending > 0
                            ? `目前沒有需要處理的人力缺額；另有 ${summary.pending} 項「待補資料」（開診表未設定），可按上方按鈕展開。`
                            : '目前沒有任何人力問題。'}
                    </div>
                ) : (
                    <div className="sc-scroll">
                        <table className="sc-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '130px' }}>日期／時段</th>
                                    <th style={{ width: '76px' }}>等級</th>
                                    <th>缺什麼／為什麼</th>
                                    <th style={{ width: '104px' }}>可否補位</th>
                                    <th style={{ width: '150px' }} className="is-act">
                                        操作
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{visibleProblems.map(coverRow)}</tbody>
                        </table>
                    </div>
                )}
            </section>

            <div className="sched-foot">
                <p>
                    一個人每個時段 ⛔ 只會被算進一個職能 —— 掛兩個職能不代表可以同時補兩個缺。
                    算進哪一個的順序是：該時段的機動補位指派 ＞ 本月主要職能 ＞ 他唯一的那個職能。
                    <br />
                    機動人力 ⛔ 不會自動計入任何職能 ——
                    必須在這一頁逐日逐時段指定「本時段補哪一個職能」，而且同一時段只會計入那一個。
                    所以某位機動人員如果沒有護理師職能，護理師缺額的下拉就是空的，並會說明原因。
                </p>
            </div>

            {busy && <LoadingMask>處理中...</LoadingMask>}
        </div>
    );
};

export default SchedPreview;
